/**
 * Typed API client for Warehouse — locations, stock levels, movement ledger.
 *
 * Mirrors internal/warehouse. Stock is tracked per product variation within
 * each warehouse location; movements (incoming/outgoing/transfer) adjust it.
 */

import { apiClient } from '@/lib/apiClient';

export interface WarehouseLocation {
  id: string;
  code: string;
  name: string;
  address?: string | null;
  countryCode?: string | null;
  enabled: boolean;
}

export interface StockLevel {
  variationId: string;
  quantity: number;
  reserved: number;
  updatedAt: string;
}

export async function listWarehouses(): Promise<WarehouseLocation[]> {
  const { data } = await apiClient.get<WarehouseLocation[]>('/warehouse');
  return data;
}

export interface CreateWarehouseRequest {
  code: string;
  name: string;
  address?: string;
  countryCode?: string;
}

export async function createWarehouse(req: CreateWarehouseRequest): Promise<{ id: string }> {
  const { data } = await apiClient.post<{ id: string }>('/warehouse', req);
  return data;
}

export async function getStock(warehouseId: string): Promise<StockLevel[]> {
  const { data } = await apiClient.get<StockLevel[]>(`/warehouse/${warehouseId}/stock`);
  return data;
}

export type MovementType = 'incoming' | 'outgoing' | 'transfer_out' | 'adjustment';

export interface MovementRequest {
  warehouseId: string;
  variationId: string;
  movementType: MovementType;
  quantity: number;
  reference?: string;
  notes?: string;
}

export async function recordMovement(req: MovementRequest): Promise<{ id: string }> {
  const { data } = await apiClient.post<{ id: string }>('/warehouse/movements', req);
  return data;
}
